import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiClient } from '@core/services/api-client';
import { Button } from '@shared/components/button/button';
import { Item } from '@shared/components/item/item';
import { Select, SelectItem } from '@shared/components/select/select';
import { AllProblems, Problem } from '@shared/models/problem';
import { TestSet } from '@shared/models/test-sets';

@Component({
  selector: 'app-view-page',
  imports: [Select, Item, Button, RouterLink],
  templateUrl: './view-page.html',
})
export class ViewPage implements OnInit {
  // Injector
  private readonly apiClient = inject(ApiClient);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  // Constants
  private readonly DIFFICULTY_OPTIONS: SelectItem[] = [
    { value: 'senior', label: 'Senior' },
    { value: 'mid', label: 'Middle' },
    { value: 'junior', label: 'Junior' },
  ];

  // State
  readonly isLoading = signal<boolean>(true);

  // Test set
  testSet = signal<TestSet | null>(null);

  // Problem
  readonly allProblems = signal<Problem[]>([]);
  readonly selectedProblems = signal<Problem[]>([]);
  // Selector
  readonly selectItems = signal<SelectItem[]>(this.DIFFICULTY_OPTIONS);
  readonly selectedDifficulty = signal<SelectItem>(this.DIFFICULTY_OPTIONS[0]);

  async ngOnInit(): Promise<void> {
    try {
      const problems: AllProblems = await this.apiClient.getAllProblems();
      this.allProblems.set(problems.problems);

      const testSetId = this.getTestSetIdFromParams();

      if (!testSetId) {
        this.router.navigate(['/test-sets']);
        return;
      }
      await this.initializeViewMode(testSetId);
    } catch (error) {
      console.error('Error loading problems:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  private getTestSetIdFromParams(): string {
    let testSetId = '';
    this.route.paramMap.subscribe(params => {
      testSetId = params.get('testSetId') || '';
    });
    return testSetId;
  }

  private async initializeViewMode(testSetId: string): Promise<void> {
    // Load the test set data for viewing
    const testSet = await this.apiClient.getTestSetDataByTestSetId(testSetId);

    this.testSet.set(testSet);
    this.setDifficultyFromTestSet(testSet);
    this.setProblemsFromTestSet(testSet);
  }

  private setDifficultyFromTestSet(testSet: TestSet): void {
    const matchedDifficulty = this.DIFFICULTY_OPTIONS.find(
      option => option.value === testSet.difficulty
    );

    if (matchedDifficulty) {
      this.selectedDifficulty.set(matchedDifficulty);
    }
  }

  private setProblemsFromTestSet(testSet: TestSet): void {
    const currentProblems = testSet.problems ?? [];

    const matchedProblems = this.allProblems().filter(problem =>
      currentProblems.some(
        testSetProblem => testSetProblem.problemId === problem.problemId
      )
    );

    this.selectedProblems.set(matchedProblems);
  }
}
